import React, { Component } from 'react';

import classnames from 'classnames';
import { Code2, GripVertical, Lock, Trash } from 'lucide-react';
import PropTypes from 'prop-types';

import Toggle from 'common/common/Toggle';
import Tooltip from 'common/common/Tooltip';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { RouteContext, RouterContext } from 'common/containers/RouterContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import ControlledDropdown from 'common/ControlledDropdown';
import Draggable from 'common/draganddrop/Draggable';
import DropArea from 'common/draganddrop/DropArea';
import generateRandomID from 'common/generateRandomID';
import Button from 'common/inputs/Button';
import TextInput from 'common/inputs/TextInput';
import KeyCodes from 'common/KeyCodes';
import AccessModal from 'common/modals/AccessModal';
import ConfirmModal from 'common/modals/ConfirmModal';
import StatusColorPicker from 'common/statuses/components/StatusColorPicker';
import StatusColors from 'common/statuses/StatusColors';
import StatusType from 'common/statuses/StatusTypes';
import AdminFeatureUpsell from 'common/subdomain/admin/AdminFeatureUpsell';
import IconButtonV2 from 'common/ui/IconButtonV2';
import groupify from 'common/util/groupify';
import hasPermission from 'common/util/hasPermission';
import mapify from 'common/util/mapify';
import withContexts from 'common/util/withContexts';
import validateInput from 'common/validateInput';

import 'css/components/subdomain/admin/_AdminStatusSettings.scss';

const Modals = {
  DeleteStatus: 'DeleteStatus',
};

class AdminStatusSettings extends Component {
  static propTypes = {
    company: PropTypes.shape({
      subdomain: PropTypes.string,
    }),
    entity: PropTypes.oneOf(['post', 'idea']),
    statuses: PropTypes.array,
    openModal: PropTypes.func,
    router: PropTypes.object,
    viewer: PropTypes.object,
    saveStatuses: PropTypes.func,
  };

  state = {
    deletingStatus: null,
    errors: [],
    modal: null,
    newStatuses: {
      [StatusType.Active]: '',
      [StatusType.Complete]: '',
      [StatusType.Closed]: '',
    },
    saving: false,
    statuses: [],
    targetStatus: null,
  };

  componentDidMount() {
    const { company, openModal, route, router, viewer } = this.props;
    if (!hasPermission('manageStatuses', company, viewer)) {
      router.replace('/admin');
      openModal(
        AccessModal,
        { requiredPermissions: ['manageStatuses'] },
        {
          allowRouteChange: true,
        },
      );
      return;
    }

    // don't show alert on exit when running SSR tests
    if (!__SSR_TEST_RUNNER__) {
      window.addEventListener('beforeunload', this.onBeforeUnload, false);
      router.setRouteLeaveHook(route, this.onRouteLeave);
    }

    const statuses = this.getStatuses();
    const initialStatus = statuses.find((status) => status.type === StatusType.Initial);
    this.setState({ statuses, targetStatus: initialStatus });

    // add variable to cheaply check if statuses were updated
    this._originalStatuses = statuses;
  }

  componentDidUpdate(prevProps) {
    const { statuses } = this.props;
    if (statuses !== prevProps.statuses) {
      const statuses = this.getStatuses();
      this.setState({ statuses });

      // add variable to cheaply check if statuses were updated
      this._originalStatuses = statuses;
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload, false);
  }

  updateStatus = (updatedStatus) => {
    const { statuses } = this.state;

    const updatedStatuses = statuses.reduce((updatedStatuses, status) => {
      const isUpdated = status.tempID === updatedStatus.tempID;
      return updatedStatuses.concat(isUpdated ? updatedStatus : status);
    }, []);

    this.setState({ statuses: updatedStatuses });
  };

  getStatuses = () => {
    const { statuses } = this.props;
    return statuses.map((status) => ({
      _id: status._id,
      color: status.color,
      delete: false,
      name: status.name,
      new: false,
      showInPublicRoadmap: status.showInPublicRoadmap,
      targetStatus: null,
      tempID: status._id,
      type: status.type,
    }));
  };

  getErrors = () => {
    const { statuses } = this.state;
    const nonDeletedStatuses = statuses.filter((status) => !status.delete);
    const errors = [];

    const repeatedStatuses = Object.values(groupify(nonDeletedStatuses, 'name')).find(
      (list) => list.length > 1,
    );
    if (repeatedStatuses) {
      const statusName = repeatedStatuses[0].name;
      errors.push(
        `The status ${statusName} is ${repeatedStatuses.length} times in the list, and should be unique.`,
      );
    }

    const statusTypes = Object.keys(mapify(nonDeletedStatuses, 'type'));
    const emptyTypes = Object.keys(StatusType).filter((type) => !statusTypes.includes(type));
    const isPlural = emptyTypes.length > 1;
    if (emptyTypes.length > 0 && isPlural) {
      errors.push(`The types "${emptyTypes.join(', ')}" have no statuses on them`);
    } else if (emptyTypes.length > 0 && !isPlural) {
      errors.push(`The type "${emptyTypes[0]}" has no statuses on it`);
    }

    const isRoadmapIncorrect =
      nonDeletedStatuses.filter((status) => status.showInPublicRoadmap).length !== 3;
    if (this.props.showRoadmap && isRoadmapIncorrect) {
      errors.push('Exactly 3 statuses need to be selected for display in the public roadmap');
    }

    return errors;
  };

  getRandomColor = () => {
    const colorList = Object.keys(StatusColors);
    const color = colorList[Math.floor(Math.random() * colorList.length)];
    const lightList = Object.keys(StatusColors[color]);
    const light = lightList[Math.floor(Math.random() * lightList.length)];

    return StatusColors[color][light];
  };

  onBeforeUnload = (e) => {
    const prompt = this.onRouteLeave();
    if (!prompt) {
      return;
    }

    const event = e || window.event;
    event.returnValue = prompt;
    return prompt;
  };

  onCloseModal = () => {
    this.setState({ deletingStatus: null, modal: null });
  };

  onColorSelected = (color, status) => {
    this.updateStatus({ ...status, color });
  };

  onDeleteStatusConfirmed = (deletedStatus, targetStatus) => {
    const { statuses } = this.state;

    // if there are any statuses with the deleted status as target, update their
    // target to the new target status instead. if there's no target status, default
    // to open.
    const statusesToUpdate = statuses.filter(
      (status) => status.delete && status.targetStatus === deletedStatus.tempID,
    );
    const statusesToUpdateMap = mapify([...statusesToUpdate, deletedStatus], 'tempID');
    const initialStatus = statuses.find((status) => status.type === StatusType.Initial);

    const updatedStatuses = statuses.reduce((updatedStatuses, status) => {
      const isUpdated = statusesToUpdateMap[status.tempID];
      const targetStatusTempID = targetStatus?.tempID || initialStatus.tempID;
      return updatedStatuses.concat(
        isUpdated ? { ...status, delete: true, targetStatus: targetStatusTempID } : status,
      );
    }, []);

    // update statuses and close modal
    this.setState({
      deletingStatus: null,
      modal: null,
      statuses: updatedStatuses,
    });
  };

  onCreateNewStatus = (statusName, statusType) => {
    if (!validateInput.companyPostStatus.name(statusName)) {
      this.setState({
        errors: ['Statuses must have at least 1 character, and a maximum length of 30 characters'],
      });
      return;
    }

    this.onNewStatus(statusName, statusType);
    this.setState(({ newStatuses }) => {
      return {
        newStatuses: {
          ...newStatuses,
          [statusType]: '',
        },
      };
    });
  };

  onDeleteStatus = (status) => {
    this.setState({ deletingStatus: status, modal: Modals.DeleteStatus });
  };

  onNameUpdated = (name, status) => {
    const statusName = name.toLowerCase();
    if (!validateInput.companyPostStatus.name(statusName)) {
      this.setState({
        errors: ['Statuses must have at least 1 character, and a maximum length of 30 characters'],
      });
      return;
    }

    this.updateStatus({ ...status, name: statusName });
  };

  onNewStatus = (name, type) => {
    const { statuses } = this.state;
    const publicRoadmapColumns = statuses.filter(
      (status) => !status.delete && status.showInPublicRoadmap,
    ).length;

    const newStatus = {
      color: this.getRandomColor(),
      delete: false,
      name,
      new: true,
      showInPublicRoadmap: publicRoadmapColumns < 3,
      tempID: generateRandomID(),
      type,
    };

    this.setState({ statuses: statuses.concat(newStatus) });
  };

  onNewStatusChange = (e, statusType) => {
    const statusName = e.target.value;
    this.setState(({ newStatuses }) => {
      return {
        newStatuses: {
          ...newStatuses,
          [statusType]: statusName,
        },
      };
    });
  };

  onNewStatusKeydown = (e, statusType) => {
    const statusName = e.target.value;
    if (e.keyCode === KeyCodes.Enter) {
      this.onCreateNewStatus(statusName, statusType);
      return;
    }
  };

  onReorderStatus = (fromStatusTempID, intoStatusTempID, { order }) => {
    const { statuses } = this.state;
    const fromStatus = statuses.find((status) => status.tempID === fromStatusTempID);
    const intoStatus = statuses.find((status) => status.tempID === intoStatusTempID);

    // if trying to move a status to the same position, do nothing
    if (fromStatusTempID === intoStatusTempID) {
      return null;
    }

    // if trying to move the last status outside its type, do nothing
    const statusesInType = statuses.filter((status) => status.type === fromStatus.type);
    if (statusesInType.length === 1) {
      return null;
    }

    const updatedStatuses = statuses.reduce((updatedStatuses, status) => {
      if (status.tempID === intoStatusTempID) {
        const updatedStatus = { ...fromStatus, type: intoStatus.type };
        const appendableStatuses = {
          after: [intoStatus, updatedStatus],
          before: [updatedStatus, intoStatus],
        }[order];

        return updatedStatuses.concat(appendableStatuses);
      }

      return status.tempID === fromStatusTempID ? updatedStatuses : updatedStatuses.concat(status);
    }, []);
    this.setState({ statuses: updatedStatuses });
  };

  onRouteLeave = () => {
    const { statuses } = this.state;

    const hasUnsavedWork = this._originalStatuses !== statuses;
    if (hasUnsavedWork) {
      return 'Your statuses have unsaved changes. Are you sure you want to leave?';
    }
  };

  onSave = async () => {
    const { saving, statuses } = this.state;
    if (saving) {
      return;
    }

    const errors = this.getErrors();
    if (errors.length) {
      this.setState({ errors });
      return;
    }

    this.setState({ errors: [], saving: true });

    const { error } = await this.props.saveStatuses(statuses);
    if (error) {
      this.setState({ errors: [error], saving: false });
    } else {
      this.setState({ errors: [], saving: false });
    }
  };

  onSelectTargetStatus = (statusTempID) => {
    const { statuses } = this.state;
    const targetStatus = statuses.find((status) => status.tempID === statusTempID);
    this.setState({ targetStatus });
  };

  onToggleColumnRoadmap = (showInPublicRoadmap, status) => {
    this.updateStatus({ ...status, showInPublicRoadmap });
  };

  renderDeleteConfirmationMessage = () => {
    const { deletingStatus, statuses, targetStatus } = this.state;

    const initialStatus = statuses.find((status) => status.type === StatusType.Initial);
    const options = statuses
      .filter((status) => {
        const isDeleting = status.tempID === deletingStatus.tempID;
        const isDeleted = status.delete;
        return !isDeleting & !isDeleted;
      })
      .map((status) => {
        return {
          name: status.tempID,
          render: (
            <div className="statusName" style={{ color: status.color }}>
              {status.name}
            </div>
          ),
        };
      });
    const targetOption = options.find((option) => option.name === targetStatus.tempID);

    return (
      <div className="renderDeleteConfirmationMessage">
        <div>
          Every {this.props.entity} with the status "{deletingStatus.name}" will be switched to the
          following status:
        </div>
        <ControlledDropdown
          className="statusesDropdown"
          dropdownClassName="statusDropdownPortal"
          onChange={this.onSelectTargetStatus}
          options={options}
          selectedName={targetOption?.name || initialStatus.tempID}
        />
        <div className="note">NOTE: Click on "Save" to confirm this change.</div>
      </div>
    );
  };

  renderDeleteModal() {
    const { deletingStatus, modal, statuses, targetStatus } = this.state;
    if (modal !== Modals.DeleteStatus) {
      return null;
    }

    // don't ask for confirmation if the status didn't exist before and no other
    // status was pointing to it.
    const shouldConfirm =
      deletingStatus._id ||
      statuses.find((status) => status.targetStatus === deletingStatus.tempID);
    if (!shouldConfirm) {
      this.onDeleteStatusConfirmed(deletingStatus);
      return;
    }

    return (
      <ConfirmModal
        closeModal={this.onCloseModal}
        message={this.renderDeleteConfirmationMessage()}
        onConfirm={() => this.onDeleteStatusConfirmed(deletingStatus, targetStatus)}
        submitButtonValue="delete status"
        useModalPortal={true}
      />
    );
  }

  renderError() {
    const { errors } = this.state;
    if (!errors.length) {
      return null;
    }

    return (
      <div className="errors">
        {errors.map((error) => (
          <div className="error" key={error}>
            {error}
          </div>
        ))}
      </div>
    );
  }

  renderChanges() {
    const { statuses } = this.state;
    if (!statuses.length) {
      return null;
    }

    const statusMap = mapify(statuses, 'tempID');
    const deleteStatusChanges = statuses
      .filter((status) => status._id && status.delete)
      .map((status) => {
        const targetStatus = statusMap[status.targetStatus];
        return (
          <li className="change" key={status.tempID}>
            All {this.props.entity}s previously set to "{status.name}" will be set to "
            {targetStatus.name}".
          </li>
        );
      });

    const newStatusChanges = statuses
      .filter((status) => status.new && !status.delete)
      .map((status) => {
        return (
          <li className="change" key={status.tempID}>
            A new status "{status.name}" will be created.
          </li>
        );
      });

    const changes = [...deleteStatusChanges, ...newStatusChanges];
    if (changes.length < 1) {
      return null;
    }

    return (
      <div className="changes">
        <span className="changeTitle">changes</span>
        <ul className="changeList">{changes}</ul>
      </div>
    );
  }

  renderLockedStatus = (status) => {
    return (
      <div className="lockedStatusContainer" key={status.tempID} style={{ color: status.color }}>
        <StatusColorPicker
          onColorSelected={(color) => this.onColorSelected(color, status)}
          selectedColor={status.color}
        />
        <div className="statusName">{status.name}</div>
        <Lock size={12} />
      </div>
    );
  };

  renderStatus = (status, statuses) => {
    const isDraggable = statuses.length > 1;
    const statusContainer = (
      <div className="statusContainer" style={{ color: status.color }}>
        {isDraggable && (
          <DropArea
            className="dropArea"
            onDrop={(statusTempID) =>
              this.onReorderStatus(statusTempID, status.tempID, { order: 'before' })
            }
          />
        )}
        <div className="left">
          <GripVertical
            size={18}
            className={classnames('statusDrag', { disabled: !isDraggable })}
            strokeWidth={1}
          />
          <StatusColorPicker
            onColorSelected={(color) => this.onColorSelected(color, status)}
            selectedColor={status.color}
          />
          <TextInput
            className="statusName"
            defaultValue={status.name}
            style={{ color: status.color }}
            onChange={(e) => this.onNameUpdated(e.target.value, status)}
          />
        </div>
        <div className="right">
          <Tooltip className="statusCode" position="top" value={status.name}>
            <Code2 className="statusCodeIcon" size={18} />
          </Tooltip>
          {this.props.showRoadmap && (
            <Toggle
              onToggle={(showInPublicRoadmap) =>
                this.onToggleColumnRoadmap(showInPublicRoadmap, status)
              }
              value={status.showInPublicRoadmap}
            />
          )}
          <IconButtonV2
            onClick={() => this.onDeleteStatus(status)}
            size="small"
            disabled={statuses.length === 1}
            icon={Trash}
            color="error"
            variant="plain"
            aria-label="Delete status"
          />
        </div>
      </div>
    );

    if (isDraggable) {
      return (
        <Draggable key={status.tempID} value={status.tempID}>
          {statusContainer}
        </Draggable>
      );
    }

    return statusContainer;
  };

  renderNewStatus = (statusType) => {
    const { newStatuses, statuses } = this.state;
    const statusesInType = statuses.filter((status) => status.type === statusType);
    const lastStatus = statusesInType[statusesInType.length - 1];

    return (
      <div className="newStatusContainer">
        <DropArea
          className="dropArea"
          onDrop={(statusTempID) =>
            this.onReorderStatus(statusTempID, lastStatus.tempID, { order: 'after' })
          }
        />
        <TextInput
          className="statusName"
          onChange={(e) => this.onNewStatusChange(e, statusType)}
          onKeyDown={(e) => this.onNewStatusKeydown(e, statusType)}
          placeholder="Add new status"
          value={newStatuses[statusType]}
        />
        {validateInput.companyPostStatus.name(newStatuses[statusType]) && (
          <Button
            className="createStatusButton"
            onTap={() => this.onCreateNewStatus(newStatuses[statusType], statusType)}
            value="Create"
          />
        )}
      </div>
    );
  };

  renderInitialStatuses = () => {
    const { statuses } = this.state;
    const initialStatuses = statuses.filter(
      (status) => !status.delete && status.type === StatusType.Initial,
    );

    return (
      <div className="statusSection">
        <div className="statusDescription">
          <div className="subheading">Default statuses</div>
          <div className="description">
            New {this.props.entity}s are given the default status. This cannot be&nbsp;changed.
          </div>
        </div>
        <div className="statusList">
          {initialStatuses.map((status) => this.renderLockedStatus(status, initialStatuses))}
        </div>
      </div>
    );
  };

  renderActiveStatuses = () => {
    const { statuses } = this.state;
    const activeStatuses = statuses.filter(
      (status) => !status.delete && status.type === StatusType.Active,
    );

    return (
      <div className="statusSection">
        <div className="statusDescription">
          <div className="subheading">Active statuses</div>
          <div className="description">Showing the progress of&nbsp;{this.props.entity}s.</div>
        </div>
        <div className="statusList">
          {activeStatuses.map((status) => this.renderStatus(status, activeStatuses))}
          {this.renderNewStatus(StatusType.Active)}
        </div>
      </div>
    );
  };

  renderCompleteStatuses = () => {
    const { statuses } = this.state;
    const completeStatuses = statuses.filter(
      (status) => !status.delete && status.type === StatusType.Complete,
    );

    return (
      <div className="statusSection">
        <div className="statusDescription">
          <div className="subheading">Complete statuses</div>
          <div className="description">
            To be used as a final status for {this.props.entity}s. These {this.props.entity}s are
            deprioritized when Canny suggests similiar&nbsp;{this.props.entity}s.
          </div>
        </div>
        <div className="statusList">
          {completeStatuses.map((status) => this.renderStatus(status, completeStatuses))}
          {this.renderNewStatus(StatusType.Complete)}
        </div>
      </div>
    );
  };

  renderClosedStatuses = () => {
    const { statuses } = this.state;
    const closedStatuses = statuses.filter(
      (status) => !status.delete && status.type === StatusType.Closed,
    );

    return (
      <div className="statusSection">
        <div className="statusDescription">
          <div className="subheading">Closed statuses</div>
          <div className="description">
            For {this.props.entity}s that will not be completed. These {this.props.entity}s are
            deprioritized when Canny suggests similar&nbsp;{this.props.entity}s.
          </div>
        </div>
        <div className="statusList">
          {closedStatuses.map((status) => this.renderStatus(status, closedStatuses))}
          {this.renderNewStatus(StatusType.Closed)}
        </div>
      </div>
    );
  };

  renderStatuses = () => {
    return (
      <div className="section">
        <div className="statusSectionList">
          {this.renderInitialStatuses()}
          {this.props.showRoadmap && (
            <div className="roadmapHint">
              <span className="roadmapHintTitle">Roadmap columns</span>
              <span>Select 3</span>
            </div>
          )}
          {this.renderActiveStatuses()}
          {this.renderCompleteStatuses()}
          {this.renderClosedStatuses()}
        </div>
      </div>
    );
  };

  render() {
    const { company } = this.props;
    const { saving } = this.state;

    if (!company.features.customStatuses) {
      return (
        <div className="adminStatusSettings">
          <AdminFeatureUpsell
            cta="Customize statuses to match your team's&nbsp;workflow"
            feature="customStatuses"
          />
        </div>
      );
    }

    return (
      <div className="adminStatusSettings">
        <div className="content">
          {this.renderStatuses()}
          {this.renderChanges()}
          <div className="buttonContainer">
            {this.renderError()}
            <Button buttonType="cannyButton" loading={saving} onTap={this.onSave} value="save" />
          </div>
        </div>
        {this.renderDeleteModal()}
      </div>
    );
  }
}

export default withContexts({
  company: CompanyContext,
  openModal: OpenModalContext,
  router: RouterContext,
  route: RouteContext,
  viewer: ViewerContext,
})(AdminStatusSettings);
